import { Flex, IconButton } from '@chakra-ui/react';
import { IconEye, IconEyeOff } from '@tabler/icons';
import {
	CellValueChangedEvent,
	ClientSideRowModelModule,
	ColDef,
	colorSchemeLightWarm,
	CustomFilterModule,
	ModuleRegistry,
	NumberEditorModule,
	SortChangedEvent,
	themeQuartz,
	ValidationModule,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AlertComponent } from 'components/alerts/AlertComponent';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import ExportCSVMenuButton from 'components/buttons/exportButtons/ExportCSVMenuButton';
import { ISelectedFilterBody } from 'components/filter/models/TableFilterModels';
import { TableFilter } from 'components/filter/TableFilter';
import { Loading360 } from 'components/loading/Loading360';
import { TooltipStandard } from 'components/tooltip/TooltipStandard';
import { useGlobalContext } from 'contexts/GlobalContext';
import { GRID_STYLES } from 'pages/admin/demand/planning/components/tabs/planningByLevel/components/planningByLevelTable/variables/PlanningByLevelTableConstants';
import { StrictMode, useCallback, useEffect, useMemo, useState } from 'react';
import { globalStyles } from 'theme/styles';

import ManualPaging, { IManualPaging } from '../ManualPaging';
import { ChangeSpacing } from './components/ChangeSpacing';
import {
	useGetDataGridTableWithFilters,
	useGetDataGridTableWithFiltersTotal,
} from './hooks/useGetDataGridTableWithFilters';
import {
	IAgGridTableProps,
	IButtonsDefault,
	ICellsEdit,
	IIsHighlighted,
	totalItemsPageList,
} from './types/agGrid.type';
import { agGridTableMessages } from './variables/agGridTableMessages';
import { generateColumns } from './variables/generateColumns';
import { AG_GRID_LOCALE_BR } from './variables/locale';

ModuleRegistry.registerModules([
	CustomFilterModule,
	ClientSideRowModelModule,
	ValidationModule,
	NumberEditorModule,
]);

const renderButtonsDefault = (buttons: IButtonsDefault) => {
	return (
		<Flex gap={'6px'}>
			{buttons.cancelButton}
			{buttons.updateButton}
		</Flex>
	);
};
export const AgGridTable = <Type, BodyType, Params>({
	module,
	editable = false,
	getIdCellEdit = () => '',
	otherParameters = {},
	headers,
	mutate,
	keyDefinedRow = [],
	model,
	disabledEditCell,
	convertToBody,
	cellEdit,
	setCellEdit,
	gridRef,
	filtersMap,
	canEdit,
	onCancel,
	renderButtons = renderButtonsDefault,
	noDataMessage = {
		title: agGridTableMessages.MSG_NO_DATA_TITLE,
		description: agGridTableMessages.MSG_NO_DATA_DESCRIPTION,
	},
	menuList = [],
	nonexistentHierarchyCombinations,
	setNonexistentHierarchyCombinations,
	gridOptions,
	status,
}: IAgGridTableProps<Type, BodyType, Params>) => {
	const [cellEditCurrent, setCellEditCurrent] = useState<ICellsEdit<Type>>(
		{}
	);
	const [cols, setCols] = useState<ColDef[]>([]);
	const [rowData, setRowData] = useState<Type[]>([]);
	const [rowTotal, setRowTotal] = useState<Type & IIsHighlighted>();
	const [order, setOrder] = useState<Array<[string, 'desc' | 'asc']>>([]);
	const [filters, setFilters] = useState<ISelectedFilterBody[]>([]);
	const {
		configsCompany: { precisao_decimal },
	} = useGlobalContext();
	const onSuccess = () => {
		setCellEditCurrent({});
	};
	useEffect(() => {
		const widthGrid = window.innerWidth - 68;

		if (headers && cols.length === 0) {
			const cols = generateColumns<Type>({
				headers,
				quantityDigits: precisao_decimal,
				widthGrid,
				disabledEditCell,
				canEdit,
			});

			setCols(cols);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [headers, disabledEditCell, canEdit]);
	const [pagination, setPagination] = useState<IManualPaging>({
		page: 0,
		size: 100,
		totalPages: 0,
	});
	const responseConvertToBody = convertToBody?.(cellEdit);
	const edited = responseConvertToBody?.insert || [];
	const { data, isFetching: isLoading } = useGetDataGridTableWithFilters<
		Type,
		BodyType
	>(
		{
			module,
			filters,
			page: pagination.page,
			size: pagination.size,
			order,
			edited,
			nonexistentHierarchyCombinations,
			status,
			...otherParameters,
		},
		onSuccess
	);
	const { data: dataTotal } = useGetDataGridTableWithFiltersTotal<
		Type,
		BodyType
	>(
		{
			module,
			filters,
			page: pagination.page,
			size: pagination.size,
			order,
			edited,
			status,
			...otherParameters,
		},
		onSuccess
	);
	const getOrderValid = useCallback(
		(idOrder: string) => {
			const order = headers.find(header =>
				idOrder.includes(header.field)
			);
			return order?.field;
		},
		[headers]
	);
	const onSortChange = useCallback(
		({ columns }: SortChangedEvent<Type, any>) => {
			const newOrder: Array<[string, 'desc' | 'asc']> = [];

			columns?.forEach(col => {
				const colId = col.getColId();
				const sort = col.getSort()
					? (col.getSort() as 'desc' | 'asc')
					: 'asc';
				const field = getOrderValid(colId);
				if (field) {
					newOrder.push([field, sort]);
				}
			});
			setOrder(newOrder as any);
		},
		[getOrderValid]
	);
	const refreshData = useCallback(() => {
		setRowData(data?.data || []);
	}, [data?.data]);

	useEffect(() => {
		if (data?.data) {
			refreshData();
			const totalPages = Math.ceil(+data?.count / +pagination.size);
			setPagination(prev => ({
				...prev,
				page:
					data.data.length === 0 && prev.page !== 0
						? totalPages - 1
						: prev.page,
				totalPages,
			}));
		}
	}, [data, pagination.size, refreshData]);
	const updateTotalEditable = useCallback(
		(isEditing: boolean) => {
			const total = dataTotal?.total;
			if (editable && total && cols.length && cellEditCurrent) {
				const labelTotalColumnsNotEditable = {};
				cols.forEach(col => {
					if (!col.editable) {
						labelTotalColumnsNotEditable[col.field || ''] = 'TOTAL';
					}
				});
				const newRowTotal: Type & IIsHighlighted = {
					...labelTotalColumnsNotEditable,
					month_1: 0,
					month_2: 0,
					month_3: 0,
					month_4: 0,
					month_7: 0,
					month_8: 0,
					month_9: 0,
					month_10: 0,
					month_11: 0,
					month_12: 0,
					total: 0,
					...(total as Type),
					isHighlighted: true,
				};
				if (isEditing && Object.keys(cellEditCurrent).length > 0) {
					Object.values(cellEditCurrent).forEach(cell => {
						const key = cell.colName;
						newRowTotal[key] += (+cell.value -
							cell.oldValue) as any;
						newRowTotal['total'] += (+cell.value -
							cell.oldValue) as any;
					});
				}
				setRowTotal(newRowTotal as any);
			}
		},
		[cellEditCurrent, cols, dataTotal?.total, editable]
	);

	useEffect(() => {
		updateTotalEditable(true);
	}, [cols, cellEditCurrent, updateTotalEditable]);
	const onChangeValue = (a: CellValueChangedEvent<Type, any>) => {
		const { colDef, value, data, oldValue } = a;
		if (editable && data[keyDefinedRow[0]]) {
			const colName = colDef.field as unknown as keyof Type;
			const id = getIdCellEdit(data, colName);
			const oldValueSaved = cellEditCurrent[id]?.oldValue;
			const newValueSaved = {
				id,
				value,
				data,
				colName,
				oldValue:
					oldValueSaved !== undefined ? oldValueSaved : oldValue,
			};

			setCellEdit({
				...cellEdit,
				[id]: newValueSaved,
			});
			setCellEditCurrent({
				...cellEditCurrent,
				[id]: newValueSaved,
			});
		} else {
			updateTotalEditable(false);
		}
	};

	const statusBar = useMemo(() => {
		return {
			statusPanels: [
				{ statusPanel: 'agTotalRowCountComponent', align: 'left' },
				{
					statusPanel: 'agAggregationComponent',
					statusPanelParams: {
						aggFuncs: ['count', 'min', 'max', 'avg', 'sum'],
					},
				},
			],
		};
	}, []);
	const [fontSize, setFontSize] = useState(14);
	const [spacing, setSpacing] = useState(8);
	const theme = useMemo(
		() =>
			themeQuartz.withPart(colorSchemeLightWarm).withParams({ fontSize }),
		[fontSize]
	);
	const Loading360Overlay = useMemo(() => {
		return () => (
			<Loading360
				size={48}
				variant='gradient'
				table={true}
				message={true}
			/>
		);
	}, []);
	const isShowPlanning = useMemo(() => {
		return status && status !== 'none';
	}, [status]);
	return (
		<Flex
			style={{
				flexGrow: 1,
				width: '100%',
				flexDirection: 'column',
				gap: '5x',
			}}
		>
			{model}
			<Flex>
				{isShowPlanning && (
					<>
						<Flex
							paddingY={'10px'}
							marginLeft={'10px'}
							flexDirection={'row'}
							zIndex={4}
							grow={1}
						>
							<TooltipStandard
								label={`${
									nonexistentHierarchyCombinations
										? agGridTableMessages.MSX_HIDE_ZERO_HIERARCHY_COMBINATIONS
										: agGridTableMessages.MSX_SHOW_ZERO_HIERARCHY_COMBINATIONS
								}`}
							>
								<IconButton
									aria-label='Opções de filtro'
									icon={
										nonexistentHierarchyCombinations ? (
											<IconEye />
										) : (
											<IconEyeOff />
										)
									}
									color={'easyBLUE.300'}
									border={'1px solid'}
									borderColor='easyBrandMenu.200'
									onClick={() => {
										setNonexistentHierarchyCombinations(
											!nonexistentHierarchyCombinations
										);
									}}
								/>
							</TooltipStandard>
							<TableFilter
								filtersMap={filtersMap}
								module={module}
								setFilters={setFilters}
								filters={filters}
								searchParams={{
									...otherParameters,
									nonexistent_hierarchy_combinations:
										nonexistentHierarchyCombinations,
								}}
								editable={true}
							/>
						</Flex>
						<Flex
							alignItems={'center'}
							gap={'8px'}
						>
							<ChangeSpacing
								spacing={spacing}
								fontSize={fontSize}
								setFontSize={setFontSize}
								setSpacing={setSpacing}
							/>
							{canEdit && (
								<Flex gap={'6px'}>
									{renderButtons({
										cancelButton: (
											<ButtonComponent
												title={'Cancelar'}
												type={'ghost'}
												onClick={onCancel}
											/>
										),
										updateButton: (
											<ButtonComponent
												title={'Atualizar Plano'}
												type={'primary'}
												onClick={() => {
													mutate?.(cellEdit, () => {
														setCellEditCurrent({});
														setCellEdit({});
													});
												}}
											/>
										),
									})}
								</Flex>
							)}
							<ExportCSVMenuButton
								menuList={[
									{
										label: 'Exportar plano da página atual (dados visíveis) para CSV',
										onClick: () => {
											gridRef.current!.api.exportDataAsCsv();
										},
									},
									{
										label: 'Exportar plano da página atual (dados visíveis) para Excel',
										onClick: () => {
											gridRef.current!.api.exportDataAsExcel();
										},
									},
									...menuList,
								]}
							/>
						</Flex>
					</>
				)}
			</Flex>
			<Flex
				grow={1}
				flexDirection={'column'}
			>
				{isShowPlanning && (
					<>
						<Flex flexGrow={1}>
							<Flex
								w={'100%'}
								minH={'calc(100vh - 200px)'}
								p={0}
								flexDirection={'column'}
							>
								<StrictMode>
									<div
										className='ag-theme-quartz'
										style={{
											width: '100%',
											height: '100%',
											...GRID_STYLES.gridContainer,
											['--ag-spacing' as any]: `${spacing}px`,
										}}
									>
										<AgGridReact<Type>
											onCellEditingStarted={params => {
												if (params.api.isPivotMode()) {
													params.api.stopEditing();
												}
											}}
											ref={gridRef}
											onSortChanged={onSortChange}
											rowData={rowData}
											columnDefs={cols}
											undoRedoCellEditing={true}
											undoRedoCellEditingLimit={10}
											theme={theme}
											onCellValueChanged={onChangeValue}
											domLayout='normal'
											pinnedTopRowData={
												rowTotal ? [rowTotal] : []
											}
											getRowStyle={params => {
												const data =
													params.data as Type &
														IIsHighlighted;
												if (
													params.node.rowIndex ===
														0 &&
													data?.isHighlighted === true
												) {
													return {
														fontWeight: 'bold',
														backgroundColor:
															globalStyles.colors
																.secondaryGray[300],
													};
												}
												return undefined;
											}}
											loading={isLoading}
											loadingOverlayComponent={
												Loading360Overlay
											}
											stopEditingWhenCellsLoseFocus={true}
											singleClickEdit={true}
											enableCellTextSelection={false}
											localeText={AG_GRID_LOCALE_BR}
											cellSelection={{
												enableHeaderHighlight: true,
												suppressMultiRanges: true,
												handle: {
													mode: 'fill',
												},
											}}
											statusBar={statusBar}
											enableFillHandle={true}
											{...gridOptions}
										/>
									</div>
								</StrictMode>
								<ManualPaging
									pagination={pagination}
									setPagination={setPagination}
									totalItemsPageList={totalItemsPageList}
								/>
							</Flex>
						</Flex>
					</>
				)}
				{!isShowPlanning && !isLoading && (
					<Flex
						w={'100%'}
						justifyContent={'center'}
					>
						<AlertComponent
							title={noDataMessage.title}
							status='info'
							description={noDataMessage.description}
						/>
					</Flex>
				)}
			</Flex>
		</Flex>
	);
};
